import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from 'react';
import { useReducedMotion } from 'framer-motion';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// localStorage keys
const THEME_KEY = 'theme';
const CINEMATIC_KEY = 'cinematic-transitions';

export const ThemeProvider = ({ children }) => {
  // ---- Committed theme (what the UI is actually showing) ----
  const [isDark, setIsDark] = useState(() => {
    const saved = localStorage.getItem(THEME_KEY);
    return saved ? saved === 'dark' : true; // default to dark
  });

  // ---- User preference: cinematic video transitions on/off ----
  const [cinematicEnabled, setCinematicEnabled] = useState(() => {
    const saved = localStorage.getItem(CINEMATIC_KEY);
    return saved === null ? true : saved === 'true'; // default on
  });

  // ---- Transition orchestration ----
  // Locks the toggle for the whole duration of a transition.
  const [isTransitioning, setIsTransitioning] = useState(false);
  // Active transition descriptor (null when idle):
  //   { mode: 'video' | 'crossfade', to: boolean }
  const [transition, setTransition] = useState(null);
  // True only while the landing hero background image is on screen (top of '/').
  // Set by <HeroSection />. Gates both the cinematic video and the cinematic button.
  const [cinematicAvailable, setCinematicAvailable] = useState(false);
  // True while the hero background video overlay is on screen, so the header +
  // hero content can dim themselves (instead of being hidden) during playback.
  const [heroVideoActive, setHeroVideoActive] = useState(false);

  const prefersReducedMotion = useReducedMotion();

  // Persist + apply the `dark`/`light` class whenever the committed theme changes.
  useEffect(() => {
    localStorage.setItem(THEME_KEY, isDark ? 'dark' : 'light');
    if (isDark) {
      document.documentElement.classList.add('dark');
      document.documentElement.classList.remove('light');
    } else {
      document.documentElement.classList.add('light');
      document.documentElement.classList.remove('dark');
    }
  }, [isDark]);

  // Commit the actual theme value (swaps background image + `dark` class).
  const applyTheme = useCallback((dark) => setIsDark(dark), []);

  // Request a theme change. Guarded against spam while a transition is running.
  const toggleTheme = useCallback(() => {
    if (isTransitioning) return;
    const to = !isDark;
    setIsTransitioning(true);

    // Play the cinematic video ONLY when it makes sense:
    //  - the user enabled it, AND
    //  - the landing hero background is currently on screen, AND
    //  - the OS isn't requesting reduced motion.
    // Otherwise fall back to a fast, clean crossfade.
    if (cinematicEnabled && cinematicAvailable && !prefersReducedMotion) {
      setTransition({ mode: 'video', to });
    } else {
      applyTheme(to); // apply immediately; the crossfade masks the swap
      setTransition({ mode: 'crossfade', to });
    }
  }, [
    isTransitioning,
    isDark,
    cinematicEnabled,
    cinematicAvailable,
    prefersReducedMotion,
    applyTheme,
  ]);

  // Fully reset transition state (called once an overlay has animated out).
  const endTransition = useCallback(() => {
    setTransition(null);
    setHeroVideoActive(false);
    setIsTransitioning(false);
  }, []);

  // Toggle + persist the cinematic-transitions preference.
  const toggleCinematicEnabled = useCallback(() => {
    setCinematicEnabled((prev) => {
      const next = !prev;
      localStorage.setItem(CINEMATIC_KEY, String(next));
      return next;
    });
  }, []);

  const value = {
    isDark,
    toggleTheme,
    applyTheme,
    isTransitioning,
    transition,
    endTransition,
    cinematicEnabled,
    toggleCinematicEnabled,
    cinematicAvailable,
    setCinematicAvailable,
    heroVideoActive,
    setHeroVideoActive,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeContext;
